import type { SlashCommandContext } from './registry'

/**
 * Parse a money input like "$5k", "5,000", "5000" into integer cents.
 * Returns null if the input is not a valid amount.
 *
 * Accepts:
 *   - "$5k" / "5k"  → 500_000
 *   - "$5,000"      → 500_000
 *   - "5000"        → 500_000
 *   - "5000.50"     → 500_050
 */
export function parseMoneyToCents(raw: string | number | undefined | null): number | null {
  if (raw == null) return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? Math.round(raw * 100) : null

  const trimmed = raw.trim().replace(/[$,_]/g, '').toLowerCase()
  if (!trimmed) return null

  let multiplier = 1
  let body = trimmed

  if (body.endsWith('k')) {
    multiplier = 1_000
    body = body.slice(0, -1)
  } else if (body.endsWith('m')) {
    multiplier = 1_000_000
    body = body.slice(0, -1)
  }

  const numeric = Number(body)
  if (!Number.isFinite(numeric)) return null

  return Math.round(numeric * multiplier * 100)
}

export function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
}

export type ResolvedCampaign = {
  id: string
  name: string
  status: string
}

/**
 * Fuzzy-resolve a campaign name to a concrete row in the user's workspace.
 *
 * Resolution order (case-insensitive):
 *   1. Exact name match
 *   2. Prefix match (single hit)
 *   3. Substring match (single hit)
 *   4. Otherwise → `error` with up to 3 candidate names so the user can disambiguate.
 *
 * Soft-deleted campaigns are excluded.
 */
export async function resolveCampaign(
  query: string,
  ctx: SlashCommandContext
): Promise<{ ok: true; campaign: ResolvedCampaign } | { ok: false; error: string }> {
  const trimmed = query.trim()
  if (!trimmed) return { ok: false, error: 'Campaign name is required.' }

  const { data, error } = await ctx.supabase
    .from('campaigns')
    .select('id, name, status')
    .eq('workspace_id', ctx.workspaceId)
    .is('is_deleted', null)
    .order('updated_at', { ascending: false })
    .limit(200)

  if (error) {
    return { ok: false, error: `Could not load campaigns: ${error.message}` }
  }

  type Row = { id: string; name: string; status: string }
  const rows = ((data ?? []) as Row[]).filter(r => r.name)
  if (rows.length === 0) {
    return { ok: false, error: 'You have no campaigns yet.' }
  }

  const q = trimmed.toLowerCase()

  // 1. exact
  const exact = rows.filter(r => r.name.toLowerCase() === q)
  if (exact.length === 1) return { ok: true, campaign: exact[0] }
  if (exact.length > 1) {
    return {
      ok: false,
      error: `Multiple campaigns match "${trimmed}" exactly. Use the unique name.`,
    }
  }

  // 2. prefix
  const prefix = rows.filter(r => r.name.toLowerCase().startsWith(q))
  if (prefix.length === 1) return { ok: true, campaign: prefix[0] }

  // 3. substring
  const substring = rows.filter(r => r.name.toLowerCase().includes(q))
  if (substring.length === 1) return { ok: true, campaign: substring[0] }

  const candidates = (prefix.length > 1 ? prefix : substring).slice(0, 3).map(r => `"${r.name}"`)
  if (candidates.length === 0) {
    return { ok: false, error: `No campaign matches "${trimmed}".` }
  }
  return {
    ok: false,
    error: `Multiple campaigns match "${trimmed}": ${candidates.join(', ')}. Use a more specific name.`,
  }
}

/**
 * Normalize a user-supplied period token to a human-readable string for LLM prompts.
 * Accepts: today, week, month, all, 7d, 30d, or any freeform string.
 */
export function parsePeriod(raw: string | undefined): string {
  if (!raw) return 'this week'
  const normalized = raw.trim().toLowerCase()
  if (normalized === 'today') return 'today'
  if (normalized === 'week') return 'this week'
  if (normalized === 'month') return 'this month'
  if (normalized === 'all') return 'all time'
  const daysMatch = normalized.match(/^(\d+)d$/)
  if (daysMatch) return `the past ${daysMatch[1]} days`
  return raw.trim()
}

/**
 * Call /api/campaigns/[id]/transition with the user's session cookies (this runs
 * client-side via fetch, so cookies are attached automatically).
 */
export async function transitionCampaign(
  id: string,
  action: 'launch' | 'pause' | 'resume'
): Promise<{ ok: true; status: string; message?: string } | { ok: false; error: string }> {
  try {
    const res = await fetch(`/api/campaigns/${id}/transition`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    })
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>

    if (!res.ok) {
      const detail = typeof body.error === 'string' ? body.error : `HTTP ${res.status}`
      return { ok: false, error: detail }
    }

    return {
      ok: true,
      status: typeof body.status === 'string' ? body.status : 'unknown',
      message: typeof body.message === 'string' ? body.message : undefined,
    }
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) }
  }
}
